import { useCallback, useEffect, useState } from 'react';
import { useToast, toastFromError } from '../../components/ToastProvider';
import {
  listConnections, setConnectionKey,
  type ConnectorId, type ConnectorStatus,
} from '../../lib/growth-api';
import { formatDate } from '../../lib/time-format';

/**
 * Ops → Growth → Connections (D-009, Track 0).
 *
 * ⭐⭐ THIS IS A SETUP SCREEN, AND SETUP SCREENS ARE JUDGED ON WHETHER SOMEONE CAN FINISH
 * THE TASK WITHOUT LEAVING TO ASK A QUESTION. So each row carries what the connector is
 * FOR, what it COSTS (free stated as plainly as paid), and a link to where the key is
 * obtained — not just a name and an empty box. Two of the four are free and one is free for
 * the first ten lookups; an operator who does not know that will reasonably hesitate before
 * pasting anything.
 *
 * ⛔ THE KEY IS WRITE-ONLY, BY DESIGN, AND THE UI MUST NOT PRETEND OTHERWISE. The server
 * returns a boolean per connector — never the key, never a masked prefix, never a length.
 * So a configured row shows "Connected", not "sk-…abcd", and the input always starts empty.
 * Re-pasting replaces; there is no "reveal". If a future change adds a last-4 affordance,
 * it has to add it to the SERVER first, and that server change is the one to argue about.
 *
 * ⚠ WHY THIS IS A UI PASTE AT ALL, since it deviates from how every other secret on this
 * platform is provisioned: D-009. Jeff's rationale, verbatim — *"the data connection is low
 * risk, we are querying and reading results, there is no sensitive data involved."* All
 * four read public government or business-register data. The deviation is a judgement about
 * these four keys, not a new general pattern.
 */
export function GrowthConnectionsTab() {
  const toast = useToast();
  const [connectors, setConnectors] = useState<ConnectorStatus[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<ConnectorId | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await listConnections();
      setConnectors(res.connectors);
      setLoadError(null);
    } catch (err) {
      // ⚠ The LIST failing is a page-level state, not a toast-and-blank: a toast that has
      // been dismissed leaves an empty page with no explanation of why.
      setLoadError('Could not load the connector list.');
      const t = toastFromError(err, 'Could not load connections');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const save = async (id: ConnectorId) => {
    const key = (drafts[id] ?? '').trim();
    if (!key) return;
    setSaving(id);
    try {
      await setConnectionKey(id, key);
      // ⛔ CLEARED IMMEDIATELY on success — the pasted value must not linger in React state
      // where a later render, a devtools inspection or an error boundary could surface it.
      setDrafts((d) => ({ ...d, [id]: '' }));
      toast.push({ variant: 'success', title: 'Key saved', detail: 'The connector is now configured.' });
      await load();
    } catch (err) {
      const t = toastFromError(err, 'Could not save that key');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className="growth-connections" aria-label="Data connectors">
      <p className="growth-page-intro">
        Target validation reads four public data sources. Each needs its own API key, pasted
        here. Keys are stored encrypted and are never shown again — to replace one, paste
        the new value over it.
      </p>

      {loadError && <p className="growth-field-warning" role="alert">{loadError}</p>}

      {/* ⭐ A LOADING STATE THAT SAYS WHAT IS LOADING, per the standing directive: a
          skeleton conveys structure, the text conveys context, and they work in tandem. */}
      {connectors === null && !loadError && (
        <div className="growth-connections-loading">
          <div className="growth-skeleton-row" aria-hidden="true" />
          <div className="growth-skeleton-row" aria-hidden="true" />
          <p className="growth-field-hint">Loading connectors…</p>
        </div>
      )}

      {connectors?.map((c) => (
        <div className="growth-connector" key={c.id}>
          <div className="growth-connector-head">
            <h3 className="growth-connector-title">{c.label}</h3>
            <span
              className={c.configured ? 'growth-connector-state is-on' : 'growth-connector-state'}
            >
              {/* The dot is decorative; the WORD carries the state, so this survives a
                  colour-vision difference or a mono print. */}
              <span aria-hidden="true">●</span> {c.configured ? 'Connected' : 'Not connected'}
            </span>
          </div>

          <p className="growth-connector-purpose">{c.purpose}</p>
          <p className="growth-field-hint">
            {c.cost}{' '}
            <a href={c.signupUrl} target="_blank" rel="noopener noreferrer">
              Get a key ↗
            </a>
          </p>

          {c.problem && (
            <p className="growth-field-warning" role="alert">{c.problem}</p>
          )}

          <div className="growth-connector-entry">
            <label className="growth-field-label" htmlFor={`key-${c.id}`}>
              {c.configured ? 'Replace key' : 'API key'}
            </label>
            <input
              id={`key-${c.id}`}
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={drafts[c.id] ?? ''}
              onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
              placeholder={c.configured ? 'Paste a new key to replace' : 'Paste the key'}
              aria-label={`${c.label} API key`}
              disabled={Boolean(c.problem)}
            />
            <button
              type="button"
              className="portal-btn"
              onClick={() => void save(c.id)}
              disabled={saving === c.id || !(drafts[c.id] ?? '').trim() || Boolean(c.problem)}
            >
              {saving === c.id ? 'Saving…' : 'Save'}
            </button>
          </div>

          {c.lastUpdated && (
            <p className="growth-field-hint">
              Last updated {formatDate(c.lastUpdated)}
            </p>
          )}
        </div>
      ))}
    </section>
  );
}
